import { useState } from 'react'
import { Button, Input, Card } from '@brix/ui'
import type { CustomPlanLimits } from '@brix/types'
import { useSetTenantCustomPlan } from '@/hooks/usePlatformAdmin'

interface SetCustomPlanModalProps {
  tenantId: string
  initialLimits: CustomPlanLimits | null
  onClose: () => void
}

const DEFAULT_LIMITS: CustomPlanLimits = {
  siteLimit: 10,
  pageLimit: 50,
  postLimit: 50,
  canViewAnalytics: true,
  canUseAiAssistant: true,
  canUseProBlocks: true,
}

export function SetCustomPlanModal({ tenantId, initialLimits, onClose }: SetCustomPlanModalProps) {
  const [limits, setLimits] = useState<CustomPlanLimits>(initialLimits ?? DEFAULT_LIMITS)
  const [error, setError] = useState<string | null>(null)
  const setCustomPlan = useSetTenantCustomPlan()

  const handleSave = async () => {
    setError(null)
    try {
      await setCustomPlan.mutateAsync({ tenantId, limits })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save custom plan')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <Card padding="lg" className="w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
        <h2 className="mb-1 text-lg font-bold text-gray-900">
          {initialLimits ? 'Edit custom plan' : 'Assign custom plan'}
        </h2>
        <p className="mb-6 text-sm text-gray-500">
          Negotiated limits for this tenant — independent of the 4 fixed tiers.
        </p>

        <div className="space-y-4">
          <Input
            label="Site limit"
            type="number"
            min={0}
            value={limits.siteLimit}
            onChange={(e) => setLimits({ ...limits, siteLimit: Number(e.target.value) })}
          />
          <Input
            label="Page limit (per site)"
            type="number"
            min={0}
            value={limits.pageLimit}
            onChange={(e) => setLimits({ ...limits, pageLimit: Number(e.target.value) })}
          />
          <Input
            label="Blog post limit (per site)"
            type="number"
            min={0}
            value={limits.postLimit}
            onChange={(e) => setLimits({ ...limits, postLimit: Number(e.target.value) })}
          />
          <label htmlFor="custom-plan-analytics" className="flex items-center gap-2 text-sm text-gray-700">
            <input
              id="custom-plan-analytics"
              type="checkbox"
              aria-label="Can view analytics"
              checked={limits.canViewAnalytics}
              onChange={(e) => setLimits({ ...limits, canViewAnalytics: e.target.checked })}
            />
            Can view analytics
          </label>
          <label htmlFor="custom-plan-ai-assistant" className="flex items-center gap-2 text-sm text-gray-700">
            <input
              id="custom-plan-ai-assistant"
              type="checkbox"
              aria-label="Can use AI assistant"
              checked={limits.canUseAiAssistant}
              onChange={(e) => setLimits({ ...limits, canUseAiAssistant: e.target.checked })}
            />
            Can use AI assistant
          </label>
          <label htmlFor="custom-plan-pro-blocks" className="flex items-center gap-2 text-sm text-gray-700">
            <input
              id="custom-plan-pro-blocks"
              type="checkbox"
              aria-label="Can use Pro blocks (e.g. product catalog)"
              checked={limits.canUseProBlocks}
              onChange={(e) => setLimits({ ...limits, canUseProBlocks: e.target.checked })}
            />
            Can use Pro blocks (e.g. product catalog)
          </label>
        </div>

        {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={setCustomPlan.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={setCustomPlan.isPending}>
            Save
          </Button>
        </div>
      </Card>
    </div>
  )
}
